import React, { useEffect, useState } from "react";
import AuthService from "../services/auth.service";
import usageService from "../services/usage.service";
import { Box, Typography, Paper, Avatar, Grid, Divider, CircularProgress, Chip, Tooltip, Stack, TextField, InputAdornment, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import StorageIcon from '@mui/icons-material/Storage';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BadgeIcon from '@mui/icons-material/Badge';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';

const Profile = () => {
    const theme = useTheme();
    const [currentUser] = useState(() => AuthService.getCurrentUser());
    const [usage, setUsage] = useState(null);
    const [loadingUsage, setLoadingUsage] = useState(false);
    const [usageError, setUsageError] = useState(null);
    const [employeeSearch, setEmployeeSearch] = useState('');

    useEffect(() => {
        if (!currentUser) {
            return;
        }

        let cancelled = false;
        setLoadingUsage(true);

        usageService.getTenantUsage()
            .then((response) => {
                if (!cancelled) {
                    setUsage(response.data);
                    setUsageError(null);
                }
            })
            .catch((error) => {
                console.error("Failed to load usage stats:", error);
                if (!cancelled) {
                    setUsageError(error.response?.data?.message || "Failed to load usage statistics.");
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingUsage(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [currentUser]);

    const formatBytes = (bytes) => {
        if (bytes === undefined || bytes === null) {
            return "0 B";
        }
        const num = Number(bytes);
        if (Number.isNaN(num) || num === 0) {
            return "0 B";
        }
        const units = ['B', 'KB', 'MB', 'GB', 'TB'];
        const idx = Math.floor(Math.log(num) / Math.log(1024));
        const size = num / Math.pow(1024, idx);
        return `${size.toFixed(size >= 100 ? 0 : size >= 10 ? 1 : 2)} ${units[idx]}`;
    };

    const humanizeCount = (count) => {
        if (!count) return "0";
        if (count < 1000) return count.toString();
        if (count < 10000) return `${(count / 1000).toFixed(1)}k`;
        return `${Math.round(count / 1000)}k`;
    };

    const totals = usage?.totals || {};
    const messageTotals = totals.messages || {};
    const taskTotals = totals.tasks || {};
    const filesTotals = totals.files || {};
    const attendanceTotals = totals.attendance || {};

    const totalMessagesBytes = messageTotals.totalBytes || 0;
    const totalTasksBytes = taskTotals.totalBytes || 0;
    const totalAttendanceBytes = attendanceTotals.totalBytes || 0;
    const totalFilesBytes = filesTotals.storageBytes || 0;
    const totalCommunicationBytes = totalMessagesBytes + totalTasksBytes;
    const grandTotalBytes = totalMessagesBytes + totalTasksBytes + totalAttendanceBytes + totalFilesBytes;

    const messageTextBytes = messageTotals.textBytes || 0;
    const messageMetadataBytes = messageTotals.metadataBytes || 0;
    const taskDescriptionBytes = taskTotals.descriptionBytes || 0;
    const taskNotesBytes = taskTotals.notesBytes || 0;
    const taskMetadataBytes = taskTotals.metadataBytes || 0;

    const employeesUsage = usage?.employees || [];
    const totalPerEmployee = employeesUsage.map(emp => {
        const messageBytes = emp.messages?.totalBytes || 0;
        const attendanceBytes = emp.attendance?.totalBytes || 0;
        return {
            ...emp,
            totalBytes: messageBytes + attendanceBytes
        };
    });

    const employeesByUsage = [...totalPerEmployee].sort((a, b) => b.totalBytes - a.totalBytes);

    const normalizedSearch = employeeSearch.trim().toLowerCase();
    const filteredEmployees = normalizedSearch.length === 0
        ? employeesByUsage
        : employeesByUsage.filter((emp) => {
            const name = emp.name?.toLowerCase() || '';
            const position = emp.position?.toLowerCase() || '';
            const email = emp.email?.toLowerCase() || '';
            return name.includes(normalizedSearch) || position.includes(normalizedSearch) || email.includes(normalizedSearch);
        });

    const summaryCards = usage ? [
        {
            key: 'total',
            label: 'Total Footprint',
            value: formatBytes(grandTotalBytes),
            icon: <StorageIcon fontSize="small" />,
            accent: '#1D4ED8',
            details: [
                { label: 'Communication', value: formatBytes(totalCommunicationBytes) },
                { label: 'Attendance', value: formatBytes(totalAttendanceBytes) },
                { label: 'Uploads', value: formatBytes(totalFilesBytes) }
            ]
        },
        {
            key: 'communication',
            label: 'Communication',
            value: formatBytes(totalCommunicationBytes),
            subtitle: `${humanizeCount(messageTotals.count)} messages · ${humanizeCount(taskTotals.count)} tasks`,
            icon: <InsertCommentIcon fontSize="small" />,
            accent: '#4B5563',
            details: [
                {
                    label: 'Messages',
                    value: `${humanizeCount(messageTotals.count)} · ${formatBytes(totalMessagesBytes)}`
                },
                {
                    label: 'Msg text',
                    value: formatBytes(messageTextBytes)
                },
                {
                    label: 'Msg metadata',
                    value: formatBytes(messageMetadataBytes)
                },
                {
                    label: 'Tasks',
                    value: `${humanizeCount(taskTotals.count)} · ${formatBytes(totalTasksBytes)}`
                },
                {
                    label: 'Task desc',
                    value: formatBytes(taskDescriptionBytes)
                },
                {
                    label: 'Task notes',
                    value: formatBytes(taskNotesBytes)
                },
                {
                    label: 'Task metadata',
                    value: formatBytes(taskMetadataBytes)
                }
            ]
        },
        {
            key: 'attendance',
            label: 'Attendance',
            value: formatBytes(totalAttendanceBytes),
            subtitle: `${humanizeCount(attendanceTotals.count || 0)} records`,
            icon: <BadgeIcon fontSize="small" />,
            accent: '#b7791f',
            details: [
                { label: 'Records', value: humanizeCount(attendanceTotals.count || 0) },
                { label: 'Base', value: formatBytes(attendanceTotals.baseBytes || 0) },
                { label: 'Notes', value: formatBytes(attendanceTotals.noteBytes || 0) },
                { label: 'Scans', value: formatBytes(attendanceTotals.scanBytes || 0) }
            ]
        },
        {
            key: 'uploads',
            label: 'Uploads',
            value: formatBytes(totalFilesBytes),
            subtitle: `${humanizeCount(filesTotals.filesCount)} files in tenant directory`,
            icon: <FolderIcon fontSize="small" />,
            accent: '#7C4A1F',
            details: [
                { label: 'Files', value: humanizeCount(filesTotals.filesCount) },
                { label: 'Footprint', value: formatBytes(totalFilesBytes) }
            ]
        }
    ] : [];

    const SummaryCard = ({ icon, label, value, subtitle, accent, details = [] }) => {
        const accentColor = accent || theme.palette.primary.main;
        return (
            <Paper
                sx={{
                    p: 3,
                    borderRadius: 4,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(15,23,42,0.05)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: 2,
                    bgcolor: 'background.paper',
                    minHeight: 192,
                    flexGrow: 1
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                        sx={{
                            width: 50,
                            height: 50,
                            bgcolor: alpha(accentColor, 0.16),
                            color: accentColor,
                            border: `1px solid ${alpha(accentColor, 0.32)}`,
                            fontSize: '1.4rem'
                        }}
                    >
                        {icon}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600, letterSpacing: 0.3 }}>
                            {label}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, lineHeight: 1.2, color: 'text.primary' }}>
                            {value}
                        </Typography>
                    </Box>
                </Stack>
                {subtitle && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {subtitle}
                    </Typography>
                )}
                {details.length > 0 && (
                    <Box sx={{ display: 'grid', gap: 0.75 }}>
                        {details.map((detail) => (
                            <Box
                                key={`${label}-${detail.label}`}
                                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            >
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                    {detail.label}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                    {detail.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Paper>
        );
    };

    return (
        <Box sx={{ p: 3, margin: 'auto', maxWidth: 1200, bgcolor: 'background.default' }}>
            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        fontSize: { xs: '1.5rem', sm: '1.75rem' },
                        mb: 0.5
                    }}
                >
                    Profile
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'primary.dark',
                        fontWeight: 400,
                        fontSize: { xs: '0.95rem', sm: '1rem' }
                    }}
                >
                    Personal details and tenant usage snapshot
                </Typography>
            </Box>

            <Box sx={{ display: 'flex' }}>
                <Paper
                    sx={{
                        p: 3,
                        borderRadius: 4,
                        bgcolor: 'background.paper',
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        width: '100%'
                    }}
                >
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <Avatar
                            sx={{
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                width: 80,
                                height: 80,
                                fontSize: '2.2rem',
                                fontWeight: 700
                            }}
                        >
                            {currentUser.username?.charAt(0).toUpperCase() || '?'}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1F2937', lineHeight: 1.2 }}>
                                {currentUser.username || '—'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
                                Signed in as {currentUser.email || 'unknown'}
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {currentUser.role && (
                            <Chip
                                label={currentUser.role}
                                size="small"
                                sx={{
                                    bgcolor: 'rgba(37, 99, 235,0.18)',
                                    color: '#4B5563',
                                    fontWeight: 600,
                                    borderRadius: 2
                                }}
                            />
                        )}
                        {currentUser.company && (
                            <Chip
                                label={currentUser.company}
                                size="small"
                                sx={{
                                    bgcolor: 'rgba(148,163,184,0.16)',
                                    color: '#334155',
                                    fontWeight: 600,
                                    borderRadius: 2
                                }}
                            />
                        )}
                    </Stack>

                    <Divider />

                    <Stack
                        spacing={1.5}
                        direction={{ xs: 'column', sm: 'row' }}
                        flexWrap="wrap"
                        sx={{ rowGap: 1.5 }}
                    >
                        <Box sx={{ minWidth: { sm: '33%' } }}>
                            <Typography variant="overline" sx={{ color: '#94A3B8', letterSpacing: 1, fontWeight: 600 }}>
                                Email
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: 500 }}>
                                {currentUser.email || 'Not provided'}
                            </Typography>
                        </Box>
                        <Box sx={{ minWidth: { sm: '33%' } }}>
                            <Typography variant="overline" sx={{ color: '#94A3B8', letterSpacing: 1, fontWeight: 600 }}>
                                Company
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: 500 }}>
                                {currentUser.company || 'Not provided'}
                            </Typography>
                        </Box>
                        <Box sx={{ minWidth: { sm: '33%' } }}>
                            <Typography variant="overline" sx={{ color: '#94A3B8', letterSpacing: 1, fontWeight: 600 }}>
                                Role
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: 500 }}>
                                {currentUser.role || '—'}
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>
            </Box>

            <Box sx={{ display: 'flex', mt: 3 }}>
                <Paper
                    sx={{
                        p: 3,
                        borderRadius: 4,
                        bgcolor: 'background.paper',
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%'
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1F2937', mb: 2 }}>
                        Usage Snapshot
                    </Typography>
                    {loadingUsage ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, py: 4 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : usageError ? (
                        <Typography color="error" sx={{ fontWeight: 500 }}>
                            {usageError}
                        </Typography>
                    ) : usage ? (
                        <>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2.5,
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        sm: 'repeat(2, minmax(0, 1fr))',
                                        md: 'repeat(2, minmax(0, 1fr))',
                                        lg: 'repeat(4, minmax(0, 1fr))',
                                    }
                                }}
                            >
                                {summaryCards.map((card) => (
                                    <Box key={card.key} sx={{ display: 'flex' }}>
                                        <SummaryCard {...card} />
                                    </Box>
                                ))}
                            </Box>
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No usage data available.
                        </Typography>
                    )}
                </Paper>
            </Box>

            {usage && !loadingUsage && (
                <Paper
                    sx={{
                        mt: 4,
                        p: 3,
                        borderRadius: 4,
                        bgcolor: 'background.paper',
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 16px 32px rgba(15,23,42,0.05)'
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        spacing={2}
                        sx={{ mb: { xs: 2, md: 3 } }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1F2937' }}>
                                    Usage Breakdown
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
                                    Top consumers across messaging, tasks, and attendance
                                </Typography>
                            </Box>
                            {employeesByUsage.length > 0 && (
                                <Chip
                                    label={`${filteredEmployees.length} of ${employeesByUsage.length}`}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(148,163,184,0.16)',
                                        color: '#334155',
                                        fontWeight: 600,
                                        borderRadius: 2
                                    }}
                                />
                            )}
                        </Box>
                        {employeesByUsage.length > 0 && (
                            <TextField
                                fullWidth
                                value={employeeSearch}
                                onChange={(e) => setEmployeeSearch(e.target.value)}
                                placeholder="Search employees..."
                                variant="outlined"
                                sx={{
                                    maxWidth: 360,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '16px',
                                        backgroundColor: 'white',
                                        transition: 'box-shadow 200ms ease-in-out',
                                        '&.Mui-focused': {
                                            boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.3)',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#E8EBF0',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#D4DAE3',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            border: '1px solid transparent',
                                        },
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{ ml: 1 }}>
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    </Stack>
                    <Divider sx={{ mb: 3 }} />

                    {employeesByUsage.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No employee data available.
                        </Typography>
                    ) : filteredEmployees.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No employees match your search.
                        </Typography>
                    ) : (
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 2.5,
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(2, minmax(0, 1fr))',
                                    md: 'repeat(3, minmax(0, 1fr))'
                                }
                            }}
                        >
                            {filteredEmployees.map((emp) => (
                                <Box key={emp.id} sx={{ display: 'flex', height: '100%' }}>
                                    <Paper
                                        sx={{
                                            p: 3,
                                            borderRadius: 4,
                                            bgcolor: 'background.paper',
                                            border: '1px solid #E2E8F0',
                                            boxShadow: '0 4px 12px rgba(15,23,42,0.05)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                            height: '100%'
                                        }}
                                    >
                                        <Stack direction="row" spacing={2} alignItems="flex-start">
                                            <Avatar sx={{ bgcolor: '#F1F5F9', color: '#334155', fontWeight: 600, width: 50, height: 50 }}>
                                                {emp.name?.charAt(0).toUpperCase()}
                                            </Avatar>
                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {emp.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {emp.position || '—'}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={formatBytes(emp.totalBytes)}
                                                size="small"
                                                sx={{
                                                    bgcolor: 'rgba(37, 99, 235,0.18)',
                                                    color: '#4B5563',
                                                    fontWeight: 600,
                                                    borderRadius: 2,
                                                    height: 28
                                                }}
                                            />
                                        </Stack>
                                        <Divider />
                                        <Box sx={{ display: 'grid', gap: 1.5, flexGrow: 1 }}>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                    Messages
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: 600 }}>
                                                    {humanizeCount(emp.messages?.count || 0)} · {formatBytes(emp.messages?.totalBytes || 0)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 500 }}>
                                                    Text {formatBytes(emp.messages?.textBytes || 0)} · Metadata {formatBytes(emp.messages?.metadataBytes || 0)}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                    Attendance
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#1F2937', fontWeight: 600 }}>
                                                    Records {humanizeCount(emp.attendance?.count || 0)} · {formatBytes(emp.attendance?.totalBytes || 0)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 500 }}>
                                                    Base {formatBytes(emp.attendance?.baseBytes || 0)} · Notes {formatBytes(emp.attendance?.noteBytes || 0)} · Scans {formatBytes(emp.attendance?.scanBytes || 0)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {usage.notes && usage.notes.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="caption" sx={{ color: '#64748B' }}>
                                {usage.notes.map((note, idx) => (
                                    <span key={idx}>{note}{idx !== usage.notes.length - 1 ? ' • ' : ''}</span>
                                ))}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default Profile;




